import { WarningFilesCanNotBeAttachedGenericError } from 'owa-locstrings/lib/strings/warningfilescannotbeattachedgenericerror.locstring.json';
import loc from 'owa-localize';
import { AttachmentState } from 'owa-attachment-full-data';
import AttachmentWellViewState from 'owa-attachment-well-data/lib/schema/AttachmentWellViewState';
import { OnAttachmentDeletedCallback } from 'owa-attachment-well-data/lib/types/AttachmentDeleteQueueItem';
import clearAttachmentInfoBarMessages from 'owa-attachment-well-data/lib/utils/AttachmentQueueManager/clearAttachmentInfoBarMessages';
import { ClientItemId } from 'owa-client-ids';
import InfoBarMessageViewStateCreator from 'owa-info-bar/lib/schema/InfoBarMessageViewStateCreator';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';

import { trace } from 'owa-trace';
import {
    SmimeAttachmentQueueManagerInterface,
    getSmimeAttachmentQueueManager,
} from './SmimeAttachmentQueueManager';

export type OnSmimeAttachmentCreatedCallback = (attachment: AttachmentType) => void;

const attachmentQueueManagerMap: { [key: string]: SmimeAttachmentQueueManagerInterface } = {};
/**
 * Creates S/MIME attachments by downloading attachment content
 * @param attachmentWellViewState ViewState containing all attachments in the attachment well
 * @param parentItemId
 * @param infoBarAddHandler
 * @param infoBarRemoveHandler
 * @param attachments Array of attachments to be converted to S/MIME attachments
 * @param onAttachmentCreatedCallback Callback which run on successful S/MIME attachment creation
 */
export default function createSmimeAttachmentsViaSmimeQueueManager(
    attachmentWellViewState: AttachmentWellViewState,
    parentItemId: ClientItemId,
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void,
    infoBarRemoveHandler: (infoBarMessageIds: string[]) => void,
    attachments?: AttachmentState[],
    onAttachmentCreatedCallback?: OnSmimeAttachmentCreatedCallback
) {
    if (!parentItemId) {
        clearAttachmentInfoBarMessages(infoBarRemoveHandler);
        infoBarAddHandler('WarningFilesCanNotBeAttachedGenericError', [
            loc(WarningFilesCanNotBeAttachedGenericError),
        ]);
        return [];
    }
    const manager = getQueueManager(
        attachmentWellViewState,
        parentItemId,
        infoBarAddHandler,
        infoBarRemoveHandler
    );
    return manager.enqueueSmimeAttachmentDownload(attachments, onAttachmentCreatedCallback);
}

export function deleteSmimeAttachmentViaSmimeQueueManager(
    attachmentWellViewState: AttachmentWellViewState,
    attachmentViewState: AttachmentState,
    parentItemId: ClientItemId,
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void,
    infoBarRemoveHandler: (infoBarMessageIds: string[]) => void,
    onAttachmentDeletedCallback?: OnAttachmentDeletedCallback
) {
    const manager = getQueueManager(
        attachmentWellViewState,
        parentItemId,
        infoBarAddHandler,
        infoBarRemoveHandler
    );
    manager.deleteSmimeAttachment(attachmentViewState, onAttachmentDeletedCallback);
}

function getQueueManager(
    attachmentWellViewState: AttachmentWellViewState,
    parentItemId: ClientItemId,
    infoBarAddHandler: (
        messageId: string,
        infoBarMessageViewStateCreator: InfoBarMessageViewStateCreator
    ) => void,
    infoBarRemoveHandler: (infoBarMessageIds: string[]) => void
): SmimeAttachmentQueueManagerInterface {
    let queueManager = attachmentQueueManagerMap[parentItemId.Id];

    if (!queueManager) {
        trace.info('Creating SmimeAttachmentQueueManager');
        queueManager = getSmimeAttachmentQueueManager(
            attachmentWellViewState,
            parentItemId,
            infoBarAddHandler,
            infoBarRemoveHandler
        );
        attachmentQueueManagerMap[parentItemId.Id] = queueManager;
        queueManager.onFinishPromise.then(() => {
            trace.info('Deleting SmimeAttachmentQueueManager');
            delete attachmentQueueManagerMap[parentItemId.Id];
        });
    }

    return queueManager;
}
